import { translations, defaultLanguage, SupportedLanguage } from '@/locales';

/**
 * Utility to translate error messages outside React components
 * Used by API client and other services
 */
class ErrorTranslator {
  private currentLanguage: SupportedLanguage = defaultLanguage;

  /**
   * Set the current language for error translation
   */
  setLanguage(language: SupportedLanguage) {
    this.currentLanguage = language;
  }

  /**
   * Get current language
   */
  getLanguage(): SupportedLanguage {
    return this.currentLanguage;
  }

  /**
   * Translate an error message based on the error type or status code
   */
  translateError(error: {
    status?: number;
    message?: string;
    type?: 'network' | 'server' | 'auth';
  }): string {
    const t = translations[this.currentLanguage];

    // Check for specific error types first
    if (error.type) {
      switch (error.type) {
        case 'network':
          return t.errors.networkError;
        case 'server':
          return t.errors.serverError;
        case 'auth':
          return t.errors.authenticationExpired;
      }
    }

    // Check for specific status codes
    if (error.status) {
      switch (error.status) {
        case 401:
          return t.errors.authenticationExpired;
        case 500:
        case 502:
        case 503:
        case 504:
          return t.errors.serverError;
        default:
          // For other status codes, fall back to server message or generic error
          return error.message || t.errors.unexpectedError;
      }
    }

    // Fall back to original message or generic error
    return error.message || t.errors.unexpectedError;
  }

  /**
   * Get a specific error message by key
   */
  getErrorMessage(key: keyof typeof translations.en.errors): string {
    const t = translations[this.currentLanguage];
    return t.errors[key];
  }
}

// Export singleton instance
export const errorTranslator = new ErrorTranslator();